/**
 * The Context &amp; Memory page — hosted memory records with inspectable
 * provenance and freshness, the append-only lifecycle trail, and the
 * Focus Pack panel.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05A, over C02C's
 * `HostedMemoryStore` (`auteur_cloud/brain/memory.py`).
 *
 * PROVENANCE AND FRESHNESS ARE INSPECTABLE (required property)
 *   Every record row renders `source_ref` (a reference, never a path —
 *   rule R-1 is enforced server-side by `normalize_source_ref`),
 *   `source_type`, `observed_at`, and the server-computed `freshness`
 *   verdict (`current` / `stale` / `unknown`). Expanding a record fetches
 *   `fetchRecordProvenance` for the detail view. The UI never computes
 *   freshness itself; it renders the verdict the store produced.
 *
 * MEMORY AND AUTHORITY ARE VISUALLY DISTINCT (required property)
 *   This page is the MEMORY plane: records, trail, and the Focus Pack's
 *   disposition metadata. Authority decisions (Canon approval, operations
 *   authority, deletion/export lifecycle) live on the Project Brain page
 *   under the `pb-authority-*` plane. Cross-links say so explicitly.
 *
 * EXCLUDED/LATENT RECORDS SHOW NO SOURCE TEXT (required property)
 *   Focus Pack rows here delegate to `components/FocusPack`, whose
 *   `RENDERABLE_DISPOSITION` guard and content-free type make this
 *   structurally true; this page adds no expansion path around it.
 *
 * VISIBLE ORCHESTRATION (Builder Round P2, binding): each section reports
 * Loading / Error / Unavailable before claiming content, and a 404 renders
 * as "route not mounted" rather than an empty list pretending completeness.
 *
 * BUILD STATUS: compiled and exercised as part of the Vite customer SPA.
 */

import React, { useCallback, useEffect, useState } from 'react';

import {
  BrainApiError,
  LifecycleEventView,
  MemoryRecordView,
  SearchBrainResponse,
  createMemoryCandidate,
  fetchLifecycleTrail,
  fetchMemoryRecords,
  fetchRecordProvenance,
  searchBrain,
} from '../../api/brain';
import { FocusPackPanel } from '../../components/FocusPack';

export interface ContextMemoryPageProps {
  projectId: string;
}

type LoadState = 'loading' | 'error' | 'unavailable' | 'ready';

const FRESHNESS_CLASS: Record<MemoryRecordView['freshness'], string> = {
  current: 'cm-freshness-current',
  stale: 'cm-freshness-stale',
  unknown: 'cm-freshness-unknown',
};

export function retrievalModeLabel(mode: string): string {
  switch (mode) {
    case 'keyword_fallback':
      return 'keyword match (semantic index not ready yet)';
    case 'vector':
      return 'semantic';
    case 'mixed':
      return 'both';
    default:
      return mode;
  }
}

export function recallIndexStateLabel(state: MemoryRecordView['recall_index_state']): string {
  switch (state) {
    case 'indexed':
      return 'semantic + keyword search';
    case 'unindexed':
    case 'stale':
      return 'keyword search now; semantic after indexing';
    case 'failed':
      return 'indexing failed — keyword search only';
    case null:
    default:
      return 'not in recall';
  }
}

function RecordRow({ record }: { record: MemoryRecordView }): React.ReactElement {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState<MemoryRecordView | null>(null);
  const [detailState, setDetailState] = useState<LoadState>('loading');

  const onToggle = useCallback(() => {
    if (!expanded && detail === null) {
      setDetailState('loading');
      fetchRecordProvenance(record.record_id)
        .then((result) => { setDetail(result); setDetailState('ready'); })
        .catch((error: unknown) => {
          if (error instanceof BrainApiError && error.status === 404) setDetailState('unavailable');
          else setDetailState('error');
        });
    }
    setExpanded((previous) => !previous);
  }, [expanded, detail, record.record_id]);

  const indexLabel = recallIndexStateLabel(record.recall_index_state);

  return (
    <li
      className="cm-record-row"
      id={`record-${record.record_id}`}
      data-kind={record.kind}
      data-lane={record.lane}
    >
      <div className="cm-record-claim">
        <strong className="cm-claim-label">Claim:</strong>{' '}
        <span className="cm-claim-text">{record.claim}</span>
      </div>
      <div className="cm-record-line">
        <code className="cm-record-id">{record.record_id}</code>
        <span className="cm-record-kind">{record.kind}</span>
        <span className="cm-record-lane">{record.lane}</span>
        {/* Provenance: a reference, never a path, never the text itself. */}
        <code className="cm-source-ref">{record.source_ref}</code>
        <span className="cm-source-type">{record.source_type}</span>
        <span className={`cm-freshness ${FRESHNESS_CLASS[record.freshness]}`}>
          freshness: {record.freshness}
        </span>
        <span className="cm-recall-index-state">
          index: {indexLabel}
        </span>
        <time className="cm-valid-from" dateTime={record.valid_from ?? record.created_at}>{record.valid_from ?? record.created_at}</time>
        <button type="button" onClick={onToggle}>{expanded ? 'Hide provenance' : 'Inspect provenance'}</button>
      </div>
      {expanded && (
        <div className="cm-record-detail">
          {detailState === 'loading' && <p>Loading provenance…</p>}
          {detailState === 'unavailable' && <p>Provenance detail unavailable.</p>}
          {detailState === 'error' && <p>Provenance detail could not be loaded.</p>}
          {detailState === 'ready' && detail !== null && (
            <dl className="cm-provenance">
              <dt>Claim</dt><dd className="cm-detail-claim">{detail.claim}</dd>
              <dt>Content reference</dt><dd><code>{detail.source_ref}</code></dd>
              <dt>Source kind</dt><dd>{detail.source_type}</dd>
              <dt>Index state</dt><dd>{recallIndexStateLabel(detail.recall_index_state)}</dd>
              <dt>Valid from</dt><dd>{detail.valid_from ?? detail.created_at}</dd>
              <dt>Created at</dt><dd>{detail.created_at}</dd>
              <dt>Freshness (server verdict)</dt><dd>{detail.freshness}</dd>
            </dl>
          )}
          <RecordTrail recordId={record.record_id} />
        </div>
      )}
    </li>
  );
}

function RecordTrail({ recordId }: { recordId: string }): React.ReactElement {
  const [events, setEvents] = useState<LifecycleEventView[]>([]);
  const [state, setState] = useState<LoadState>('loading');

  useEffect(() => {
    setState('loading');
    fetchLifecycleTrail(recordId)
      .then((result) => { setEvents(result); setState('ready'); })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && error.status === 404) setState('unavailable');
        else setState('error');
      });
  }, [recordId]);

  return (
    <div className="cm-trail">
      <h5>Lifecycle trail (append-only)</h5>
      {state === 'loading' && <p>Loading…</p>}
      {state === 'unavailable' && <p>Trail unavailable.</p>}
      {state === 'error' && <p>Trail could not be loaded.</p>}
      {state === 'ready' && events.length === 0 && <p>No lifecycle events recorded.</p>}
      {state === 'ready' && events.length > 0 && (
        <ul className="cm-trail-list">
          {events.map((event) => (
            <li key={event.event_id} className="cm-trail-row">
              <span className="cm-trail-kind">{event.event_kind}</span>
              <span className="cm-trail-reason">{event.reason_code}</span>
              <span className="cm-trail-actor">by {event.actor_kind}</span>
              <time className="cm-trail-at" dateTime={event.created_at}>{event.created_at}</time>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SaveFactForm({
  projectId,
  onCandidateSaved,
}: {
  projectId: string;
  onCandidateSaved?: () => void;
}): React.ReactElement {
  const [factText, setFactText] = useState('');
  const [lane, setLane] = useState('ops');
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = factText.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setStatusMessage(null);
    setErrorMessage(null);
    try {
      await createMemoryCandidate(projectId, {
        text: trimmed,
        lane: lane.trim() || 'ops',
        kind: 'fact',
      });
      setFactText('');
      setStatusMessage(
        'Fact submitted as a candidate. It becomes memory only after a person approves it.',
      );
      if (onCandidateSaved) onCandidateSaved();
    } catch (error: unknown) {
      if (error instanceof BrainApiError && error.status === 404) {
        setErrorMessage('Submission unavailable — not found, or the Brain service is unavailable.');
      } else if (error instanceof BrainApiError) {
        setErrorMessage(`Failed to save fact: server returned status ${error.status}.`);
      } else {
        setErrorMessage('Failed to save fact.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="cm-save-fact-card" aria-label="Save a fact">
      <h3>Save a fact</h3>
      <p className="cm-save-fact-notice">
        Saving a fact creates a <strong>candidate</strong>. It becomes memory only after a person approves it. Review pending candidates in the{' '}
        <a href={`/projects/${projectId}/brain`} className="cm-pending-review-link">
          Project Brain pending-review panel
        </a>.
      </p>
      <form className="cm-save-fact-form" onSubmit={handleSubmit}>
        <label className="cm-form-label">
          Fact text
          <textarea
            className="cm-save-fact-input"
            value={factText}
            onChange={(e) => setFactText(e.target.value)}
            placeholder="Enter a fact to submit as a memory candidate"
            rows={3}
            disabled={submitting}
            required
          />
        </label>
        <div className="cm-form-row">
          <label className="cm-form-label">
            Lane
            <input
              type="text"
              className="cm-save-fact-lane"
              value={lane}
              onChange={(e) => setLane(e.target.value)}
              placeholder="e.g. ops"
              disabled={submitting}
            />
          </label>
          <button
            type="submit"
            className="cm-save-fact-button"
            disabled={submitting || !factText.trim()}
          >
            {submitting ? 'Saving candidate…' : 'Save fact'}
          </button>
        </div>
      </form>
      {statusMessage && <p className="cm-save-status cm-status-success">{statusMessage}</p>}
      {errorMessage && <p className="cm-save-status cm-status-error">{errorMessage}</p>}
    </div>
  );
}

function SearchBox({ projectId }: { projectId: string }): React.ReactElement {
  const [query, setQuery] = useState('');
  const [searchResponse, setSearchResponse] = useState<SearchBrainResponse | null>(null);
  const [searchState, setSearchState] = useState<'idle' | 'loading' | 'error' | 'unavailable' | 'ready'>('idle');
  const [searchedQuery, setSearchedQuery] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) return;
    setSearchState('loading');
    setSearchedQuery(trimmed);
    try {
      const response = await searchBrain(projectId, trimmed);
      setSearchResponse(response);
      setSearchState('ready');
    } catch (error: unknown) {
      if (error instanceof BrainApiError && error.status === 404) {
        setSearchState('unavailable');
      } else {
        setSearchState('error');
      }
    }
  };

  return (
    <div className="cm-search-card" aria-label="Search memory">
      <h3>Search memory</h3>
      <form className="cm-search-form" onSubmit={handleSearch}>
        <input
          type="search"
          className="cm-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search memories by keyword or semantic query…"
          aria-label="Search query"
        />
        <button
          type="submit"
          className="cm-search-button"
          disabled={searchState === 'loading' || !query.trim()}
        >
          {searchState === 'loading' ? 'Searching…' : 'Search'}
        </button>
      </form>

      {searchState === 'loading' && <p className="cm-search-state">Searching records…</p>}
      {searchState === 'unavailable' && (
        <p className="cm-search-state cm-search-unavailable">
          Search unavailable — not found, or the Brain service is unavailable at this deployment.
        </p>
      )}
      {searchState === 'error' && (
        <p className="cm-search-state cm-search-error">
          Search could not be performed. Please try again.
        </p>
      )}
      {searchState === 'ready' && searchResponse && (
        <div className="cm-search-results">
          <div className="cm-search-meta">
            <span className="cm-search-mode-label">
              Retrieval mode: <strong>{retrievalModeLabel(searchResponse.retrieval_mode)}</strong>
            </span>
            {searchResponse.explain && (
              <span className="cm-search-explain"> ({searchResponse.explain})</span>
            )}
          </div>
          {searchResponse.hits.length === 0 ? (
            <p className="cm-search-empty">
              {searchResponse.explain || `No search results found for "${searchedQuery}".`}
            </p>
          ) : (
            <ul className="cm-search-hit-list">
              {searchResponse.hits.map((hit) => {
                const isMemoryRecord = hit.source_ref.startsWith('memory_record:');
                const recordId = isMemoryRecord ? hit.source_ref.slice('memory_record:'.length) : null;
                return (
                  <li key={hit.chunk_id} className="cm-search-hit">
                    <p className="cm-hit-content">{hit.content}</p>
                    <div className="cm-hit-meta">
                      <span className="cm-hit-lane">lane: {hit.lane}</span>
                      <span className="cm-hit-mode">
                        mode: {retrievalModeLabel(hit.retrieval_mode)}
                      </span>
                      <span className="cm-hit-score">score: {hit.score}</span>
                      {isMemoryRecord && recordId ? (
                        <a href={`#record-${recordId}`} className="cm-hit-record-link">
                          View memory record {recordId}
                        </a>
                      ) : (
                        <code className="cm-hit-source-ref">{hit.source_ref}</code>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function ContextMemoryPage({ projectId }: ContextMemoryPageProps): React.ReactElement {
  const [records, setRecords] = useState<MemoryRecordView[]>([]);
  const [state, setState] = useState<LoadState>('loading');

  const load = useCallback(() => {
    setState('loading');
    fetchMemoryRecords(projectId)
      .then((result) => { setRecords(result); setState('ready'); })
      .catch((error: unknown) => {
        if (error instanceof BrainApiError && error.status === 404) setState('unavailable');
        else setState('error');
      });
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="cm-page" aria-label="Context and Memory">
      <header className="cm-header">
        <h2>Context &amp; Memory</h2>
        <p className="cm-subtitle">
          Hosted memory records with provenance and freshness. Canon approvals,
          operations authority, and export/deletion live on the Project Brain page —
          memory and authority are kept visibly separate.
        </p>
      </header>

      <section className="cm-memory-plane" aria-label="Memory records">
        <SaveFactForm projectId={projectId} onCandidateSaved={load} />
        <SearchBox projectId={projectId} />

        {state === 'loading' && <p className="cm-state">Loading records…</p>}
        {state === 'unavailable' && (
          <p className="cm-state">
            Memory unavailable — not found, or the Brain service is unavailable at this deployment.
            Nothing is fabricated here.
          </p>
        )}
        {state === 'error' && (
          <p className="cm-state">
            Records could not be loaded. <button type="button" onClick={load}>Retry</button>
          </p>
        )}
        {state === 'ready' && records.length === 0 && <p className="cm-state">No memory records for this project.</p>}
        {state === 'ready' && records.length > 0 && (
          <ul className="cm-record-list">
            {records.map((record) => <RecordRow key={record.record_id} record={record} />)}
          </ul>
        )}
      </section>

      <section className="cm-focus-plane" aria-label="Focus Pack">
        <FocusPackPanel projectId={projectId} />
      </section>
    </div>
  );
}

export default ContextMemoryPage;
